"use client";

import { useState, useMemo, useEffect, useCallback } from "react";
import Link from "next/link";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import DealCard from "@/components/deals/DealCard";
import CategoryVoucherStrip from "./CategoryVoucherStrip";
import TripConfigBlock from "./TripConfigBlock";
import { useSiteContext } from "@/components/providers/SiteContextProvider";
import { getTranslations } from "@/lib/i18n";
import { getLocalizedField } from "@/lib/localized-field";
import { dealsFoundWord, offersWord, offersWordInline, resultCountWithVouchers } from "@/lib/listing-count";
import { getLocalizedCategoryName, getLocalizedCountryName } from "@/lib/category-display";
import { getCurrencySymbol } from "@/lib/format-price";
import { convertEurTo } from "@/lib/currency-convert";
import {
  TRAVEL_PARTY_COOKIE_NAME,
  TRAVEL_PARTY_COOKIE_MAX_AGE_SECONDS,
  parseTravelPartyCookie,
  serializeTravelParty,
  mergeTravelPartyUpdate,
} from "@/lib/travel-party-cookie";
import { useTravelPartyFromCookie } from "@/lib/use-travel-party-cookie";
import {
  priceHistoryDisplayRuleForMarket,
  readCustomerPriceClaim,
} from "@/lib/strikethrough-gate";
import { useAvailableFixedDateCampaignCollections } from "@/lib/use-fixed-date-voucher-unavailable";
import { useListingHeal } from "./useListingHeal";

function parseMaybeJson(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function parseHotelRatings(deal) {
  const ratings = parseMaybeJson(deal.hotel_ratings ?? deal.hotelRatings);
  return ratings.map(Number).filter((n) => !isNaN(n) && n >= 1 && n <= 5);
}

function parseLengthOfStay(deal) {
  const los = parseMaybeJson(deal.length_of_stay ?? deal.lengthOfStay);
  return los.map(Number).filter((n) => !isNaN(n) && n > 0);
}

function parseBoardTypes(deal) {
  return parseMaybeJson(deal.board_type ?? deal.boardType).map((b) =>
    String(b || "").toLowerCase(),
  );
}

function getDealCountry(deal, locale) {
  const country = getLocalizedField(deal, "country", locale);
  if (country && country !== "null") {
    const raw = String(country).trim();
    // The deal's `country` field is messy (English scalars like "Germany",
    // Dutch scalars like "Hongarije", i18n-JSON without pl), so getLocalizedField
    // yields mixed NL/EN names. getLocalizedCountryName normalises EVERY form to
    // the visitor's locale (incl. nl, deduping "Germany"+"Duitsland" into one
    // chip) so the "Bestemming" filter reads Polish on .pl / German on .de, and
    // is internally consistent for applyFilters. Unknown names fall through
    // unchanged. (2026-06-24: .pl still showed Hongarije/Oostenrijk/Tsjechie.)
    return getLocalizedCountryName(raw, locale);
  }
  return null;
}

function getDealLowestPrice(deal) {
  return (
    Number(deal.lowest_price) ||
    Number(deal.lowestPrice) ||
    Number(deal.lowest_price_before_discount) ||
    Number(deal.lowestPriceBeforeDiscount) ||
    0
  );
}

function getDealDiscount(deal, marketKey) {
  const claim = readCustomerPriceClaim(deal, marketKey, { currentPriceEur: getDealLowestPrice(deal) });
  return claim?.displayRule === "badge" || claim?.displayRule === "configured_percentage"
    ? claim.savingsPct
    : 0;
}

const PRICE_BUCKETS = [
  { id: "p1", labelKey: "listing.price.lt_50", label: "Tot € 50", min: 0, max: 50 },
  { id: "p2", labelKey: "listing.price.50_100", label: "€ 50 - € 100", min: 50, max: 100 },
  { id: "p3", labelKey: "listing.price.100_150", label: "€ 100 - € 150", min: 100, max: 150 },
  { id: "p4", labelKey: "listing.price.150_250", label: "€ 150 - € 250", min: 150, max: 250 },
  { id: "p5", labelKey: "listing.price.gt_250", label: "€ 250+", min: 250, max: Infinity },
];

// Currency-aware price-bucket label (Dennis 2026-06-15 UK fix): the bucket
// bounds are round numbers in the DISPLAY currency and the label symbol
// follows the market (EUR keeps "€", GBP becomes "£"), so a UK visitor sees
// "£ 50 - £ 100" matching the pound prices on the cards instead of euro
// buckets. Comparison is done in the display currency too (see applyFilters).
function priceBucketLabel(b, t, currency) {
  const raw = t(b.labelKey, b.label);
  const sym = getCurrencySymbol(currency);
  return sym === "€" ? raw : raw.replace(/€/g, sym);
}

// Single-value night options: trip-config-driven. "5+" maps to any LoS >= 5.
const NIGHTS_TRIP_CONFIG_VALUES = new Set(["1", "2", "3", "4", "5+"]);

function nightsMatches(deal, value) {
  if (!value || value === "any") return true;
  const los = parseLengthOfStay(deal);
  if (los.length === 0) return false;
  if (value === "5+") return los.some((n) => n >= 5);
  const target = Number(value);
  if (!Number.isFinite(target)) return true;
  return los.includes(target);
}

const DISCOUNT_BUCKETS = [
  { id: "d10", labelKey: "listing.discount.10", label: "10% of meer", min: 10 },
  { id: "d20", labelKey: "listing.discount.20", label: "20% of meer", min: 20 },
  { id: "d30", labelKey: "listing.discount.30", label: "30% of meer", min: 30 },
  { id: "d40", labelKey: "listing.discount.40", label: "40% of meer", min: 40 },
];

const INCLUSION_OPTIONS = [
  { id: "breakfast", labelKey: "listing.inclusion.breakfast", label: "Ontbijt", match: (d) => parseBoardTypes(d).some((b) => b.includes("bed") || b.includes("breakfast") || b.includes("ontbijt")) },
  { id: "dinner", labelKey: "listing.inclusion.dinner", label: "Diner", match: (d) => parseBoardTypes(d).some((b) => b.includes("half") || b.includes("full") || b.includes("diner") || b.includes("dinner")) },
  { id: "activity", labelKey: "listing.inclusion.activity", label: "Activiteit/entreeticket", match: (d) => d.has_activity === true || d.hasActivity === true },
];

function sortDeals(deals, sortBy, marketKey, discountEnabled) {
  const sorted = [...deals];
  switch (sortBy) {
    case "price-asc":
      return sorted.sort((a, b) => (getDealLowestPrice(a) || 999) - (getDealLowestPrice(b) || 999));
    case "price-desc":
      return sorted.sort((a, b) => (getDealLowestPrice(b) || 0) - (getDealLowestPrice(a) || 0));
    case "discount":
      if (!discountEnabled) return sorted;
      return sorted.sort((a, b) => getDealDiscount(b, marketKey) - getDealDiscount(a, marketKey));
    case "newest":
      return sorted.sort((a, b) => (b.deals_id || 0) - (a.deals_id || 0));
    default:
      return sorted;
  }
}

function applyFilters(deals, filters, tripNights, locale, currency, effectiveRate, marketKey, discountEnabled) {
  return deals.filter((deal) => {
    if (filters.stars.length > 0) {
      const ratings = parseHotelRatings(deal);
      if (!ratings.some((r) => filters.stars.includes(r))) return false;
    }

    if (filters.destinations.length > 0) {
      const country = getDealCountry(deal, locale);
      if (!country || !filters.destinations.includes(country)) return false;
    }

    if (!nightsMatches(deal, tripNights)) return false;

    if (filters.priceBuckets.length > 0) {
      // Compare in the DISPLAY currency so the bucket bounds (round numbers in
      // the visitor's currency) line up with the prices shown on the cards.
      const price = convertEurTo(getDealLowestPrice(deal), currency, effectiveRate);
      if (!price) return false;
      const inBucket = filters.priceBuckets.some((id) => {
        const bucket = PRICE_BUCKETS.find((b) => b.id === id);
        return bucket && price >= bucket.min && price < bucket.max;
      });
      if (!inBucket) return false;
    }

    if (discountEnabled && filters.discount) {
      const bucket = DISCOUNT_BUCKETS.find((b) => b.id === filters.discount);
      if (bucket && getDealDiscount(deal, marketKey) < bucket.min) return false;
    }

    if (filters.inclusions.length > 0) {
      const ok = filters.inclusions.every((id) => {
        const opt = INCLUSION_OPTIONS.find((o) => o.id === id);
        return opt ? opt.match(deal) : true;
      });
      if (!ok) return false;
    }

    return true;
  });
}

const EMPTY_FILTER_STATE = {
  destinations: [],
  priceBuckets: [],
  inclusions: [],
  stars: [],
  discount: "",
};

function countActive(filters) {
  return (
    filters.destinations.length +
    filters.priceBuckets.length +
    filters.inclusions.length +
    filters.stars.length +
    (filters.discount ? 1 : 0)
  );
}

function persistTravelPartyCookie(update) {
  if (typeof document === "undefined") return;
  try {
    const existingRaw = document.cookie
      .split("; ")
      .find((c) => c.startsWith(`${TRAVEL_PARTY_COOKIE_NAME}=`));
    const existing = existingRaw
      ? parseTravelPartyCookie(
          decodeURIComponent(existingRaw.substring(`${TRAVEL_PARTY_COOKIE_NAME}=`.length)),
        )
      : null;
    const merged = mergeTravelPartyUpdate(existing, update);
    const value = serializeTravelParty(merged);
    if (!value) return;
    document.cookie =
      `${TRAVEL_PARTY_COOKIE_NAME}=${encodeURIComponent(value)};` +
      `max-age=${TRAVEL_PARTY_COOKIE_MAX_AGE_SECONDS};path=/;samesite=lax`;
  } catch {
    // ITP / private mode — silent fail.
  }
}

/**
 * Initial trip config: URL first, then the travel-party cookie, then defaults.
 * Pinned by category-page-filterparams-handoff and category-page-cookie-fallback
 * regression specs.
 *
 * 2026-05-20 (Dennis Figma): the same four fields are ALSO editable in the
 * sidebar trip-config block; on edit, we persist to cookie + mirror to the URL
 * so deep-link works. filterParams stays a derived projection of the live state
 * so <DealCard> hrefs reflect the visitor's latest edits.
 * 2026-05-21 fix: previously these blocks ran `Number(searchParams.get(field))`
 * directly. `searchParams.get(...)` returns `null` when the field is
 * absent, and `Number(null) === 0`. The adults branch survived this
 * because its acceptance gate is `>= 1` (0 fails through to cookie),
 * but the children branch with `>= 0` would accept the 0 → return 0
 * → cookie fallback never runs → children=2 in cookie was silently
 * dropped to 0 in DealCard hrefs once HeroSearch stopped writing
 * ?children=… to the URL (PR #717). Explicit null-check on the URL
 * value restores the URL → cookie → default chain for both fields.
 */
function initialTripConfig(searchParams, cookieFallbackParty = null) {
  const initialAdults = (() => {
    const raw = searchParams.get("adults");
    if (raw != null) {
      const fromUrl = Number(raw);
      if (Number.isInteger(fromUrl) && fromUrl >= 1) return fromUrl;
    }
    const fromCookie = Number(cookieFallbackParty?.adults);
    if (Number.isInteger(fromCookie) && fromCookie >= 1) return fromCookie;
    return 2;
  })();
  const initialChildren = (() => {
    const raw = searchParams.get("children");
    if (raw != null) {
      const fromUrl = Number(raw);
      if (Number.isInteger(fromUrl) && fromUrl >= 0) return fromUrl;
    }
    const fromCookie = Number(cookieFallbackParty?.children);
    if (Number.isInteger(fromCookie) && fromCookie >= 0) return fromCookie;
    return 0;
  })();
  const initialNights = searchParams.get("nights") || cookieFallbackParty?.nights || "any";
  const initialDate = searchParams.get("date") || cookieFallbackParty?.date || "";
  return {
    adults: initialAdults,
    children: initialChildren,
    nights: initialNights,
    date: initialDate,
  };
}

/** Client view of a category page: renders the deal grid + voucher strip with personalized pinning, and persists the travel-party cookie. */
export default function CategoryPageClient({
  deals,
  vouchers = [],
  categoryName,
  categoryDisplayName,
  pinnedDealIds = [],
  source = "category_page",
  // "fresh" (default) | "stale" | "degraded". Alleen "fresh" met 0 deals is een
  // echt lege categorie. "degraded" = de server kon de lijst niet ophalen: dan
  // tonen we een herstelstaat en proberen we het opnieuw (useListingHeal).
  listingState = "fresh",
  // true op domeinen waar lege pagina's eerlijk getoond worden (.de): vouchers
  // tellen dan als aanbod en er komt geen "geen deals"-blok naast.
  honestEmpty = false,
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { locale, currency, effectiveRate, key: marketKey } = useSiteContext();
  const { t } = getTranslations(locale);
  const [availableDeals, availableVouchers] =
    useAvailableFixedDateCampaignCollections(deals, vouchers);
  const discountSortFilterVerified = useMemo(
    () =>
      priceHistoryDisplayRuleForMarket(marketKey) === "badge" &&
      availableDeals.some((deal) => getDealDiscount(deal, marketKey) > 0),
    [availableDeals, marketKey],
  );

  const pinnedIdSet = useMemo(
    () => new Set(pinnedDealIds.map((id) => String(id))),
    [pinnedDealIds],
  );

  // Codex round-8 BUG 9 + round-12 cookie 30d UX (2026-05-09):
  // URL-first, cookie-fallback handoff so homepage HeroSearch parties
  // still reach the booking page and returning visitors keep their 30-day
  // cookie selection. See initialTripConfig above.
  //
  // Privacy-audit 26-10-2026: the server no longer passes the cookie in.
  // These pages are shared edge-cached, so the first render (server and
  // hydration) seeds from URL → defaults only, and the cookie fallback is
  // applied right after mount from this browser's own cookie.
  const initial = initialTripConfig(searchParams);
  const [tripAdults, setTripAdults] = useState(initial.adults);
  const [tripChildren, setTripChildren] = useState(initial.children);
  const [tripNights, setTripNights] = useState(initial.nights);
  const [tripDate, setTripDate] = useState(initial.date);
  const [tripExactDate, setTripExactDate] = useState(false);

  useTravelPartyFromCookie((cookieFallbackParty) => {
    const seeded = initialTripConfig(searchParams, cookieFallbackParty);
    setTripAdults(seeded.adults);
    setTripChildren(seeded.children);
    setTripNights(seeded.nights);
    setTripDate(seeded.date);
  });

  const filterParams = {
    adults: tripAdults != null ? String(tripAdults) : null,
    children: tripChildren != null ? String(tripChildren) : null,
    nights: tripNights && tripNights !== "any" ? tripNights : null,
    date: tripDate || null,
  };

  const updateTripConfig = useCallback(
    (patch) => {
      let nextAdults = tripAdults;
      let nextChildren = tripChildren;
      let nextNights = tripNights;
      let nextDate = tripDate;
      if ("adults" in patch) {
        nextAdults = patch.adults;
        setTripAdults(patch.adults);
      }
      if ("children" in patch) {
        nextChildren = patch.children;
        setTripChildren(patch.children);
      }
      if ("nights" in patch) {
        nextNights = patch.nights;
        setTripNights(patch.nights);
      }
      if ("date" in patch) {
        nextDate = patch.date;
        setTripDate(patch.date);
      }
      if ("exactDate" in patch) {
        setTripExactDate(patch.exactDate);
      }

      // Persist to ft_travel_party cookie (merges with rooms data the
      // booking flow may have written).
      persistTravelPartyCookie({
        adults: nextAdults,
        children: nextChildren,
        nights: nextNights && nextNights !== "any" ? nextNights : undefined,
        date: nextDate || undefined,
      });

      // Mirror to URL so deep-link / refresh preserves the selection.
      const next = new URLSearchParams(searchParams.toString());
      if (nextAdults != null) next.set("adults", String(nextAdults));
      if (nextChildren > 0) next.set("children", String(nextChildren));
      else next.delete("children");
      if (nextNights && nextNights !== "any") next.set("nights", nextNights);
      else next.delete("nights");
      if (nextDate) next.set("date", nextDate);
      else next.delete("date");
      const qs = next.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    },
    [tripAdults, tripChildren, tripNights, tripDate, pathname, router, searchParams],
  );

  const [filters, setFilters] = useState(EMPTY_FILTER_STATE);
  const [sortBy, setSortBy] = useState("recommended");
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);

  // Lock body scroll when mobile sheet is open
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.style.overflow = mobileSheetOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileSheetOpen]);

  const availableDestinations = useMemo(() => {
    const counts = new Map();
    availableDeals.forEach((d) => {
      const c = getDealCountry(d, locale);
      if (c) counts.set(c, (counts.get(c) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);
  }, [availableDeals, locale]);

  const availableStars = useMemo(() => {
    const set = new Set();
    availableDeals.forEach((d) =>
      parseHotelRatings(d).forEach((r) => set.add(r)),
    );
    return Array.from(set).sort((a, b) => a - b);
  }, [availableDeals]);

  const filteredDeals = useMemo(
    () =>
      sortDeals(
        applyFilters(availableDeals, filters, tripNights, locale, currency, effectiveRate, marketKey, discountSortFilterVerified),
        sortBy,
        marketKey,
        discountSortFilterVerified,
      ),
    [availableDeals, filters, tripNights, locale, currency, effectiveRate, marketKey, sortBy, discountSortFilterVerified],
  );

  const activeCount = countActive(filters);
  const resultCount = filteredDeals.length;
  // Herstelstaat: ALLEEN bij een positief storingssignaal van de server (nooit
  // afgeleid uit "0 deals") en alleen als er geen enkele deal is om te tonen.
  const showRecovery =
    listingState === "degraded" && (!Array.isArray(deals) || deals.length === 0);
  const { failed: healFailed, retry: healRetry } = useListingHeal(showRecovery);
  const listingStateAttr = showRecovery
    ? "degraded"
    : resultCount === 0 && (!Array.isArray(deals) || deals.length === 0)
      ? "empty"
      : listingState === "stale"
        ? "stale"
        : "ok";

  function toggleArrayFilter(key, value) {
    setFilters((prev) => {
      const list = prev[key];
      const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
      return { ...prev, [key]: next };
    });
  }

  function setSingleFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: prev[key] === value ? "" : value }));
  }

  function clearAll() {
    setFilters(EMPTY_FILTER_STATE);
  }

  return (
    <div className="max-w-[1320px] mx-auto px-4 py-6 lg:py-8">
      {availableVouchers.length > 0 && (
        <CategoryVoucherStrip
          vouchers={availableVouchers}
          categoryDisplayName={categoryDisplayName || categoryName}
        />
      )}

      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block w-[280px] flex-shrink-0">
          <div className="pb-2 mb-2 border-b border-gray-200">
            <TripConfigBlock
              date={tripDate}
              nights={tripNights}
              adults={tripAdults}
              childrenCount={tripChildren}
              exactDate={tripExactDate}
              onChange={updateTripConfig}
            />
          </div>
          <FilterSidebar
            filters={filters}
            setFilters={setFilters}
            toggleArrayFilter={toggleArrayFilter}
            setSingleFilter={setSingleFilter}
            availableDestinations={availableDestinations}
            availableStars={availableStars}
            activeCount={activeCount}
            onClearAll={clearAll}
            t={t}
            currency={currency}
            discountEnabled={discountSortFilterVerified}
          />
        </aside>

        <div
          className="flex-1 min-w-0"
          data-listing-state={listingStateAttr}
          aria-busy={showRecovery && !healFailed ? "true" : undefined}
        >
          {/* Mobile filter bar */}
          <div className="lg:hidden mb-4 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileSheetOpen(true)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M3 4h18M3 12h12M3 20h6" />
              </svg>
              {t("listing.filters", "Filters")}
              {activeCount > 0 && (
                <span className="bg-[#0A8DE9] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {activeCount}
                </span>
              )}
            </button>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white"
              aria-label={t("listing.sort_label", "Sorteren")}
            >
              <option value="recommended">{t("listing.sort_recommended", "Aanbevolen")}</option>
              <option value="price-asc">{t("listing.sort_price_asc", "Prijs (laag-hoog)")}</option>
              <option value="price-desc">{t("listing.sort_price_desc", "Prijs (hoog-laag)")}</option>
              {discountSortFilterVerified && (
                <option value="discount">{t("listing.sort_discount", "Korting")}</option>
              )}
              <option value="newest">{t("listing.sort_newest", "Nieuwste")}</option>
            </select>
          </div>

          {/* Desktop header row */}
          {!showRecovery && (
          <div className="hidden lg:flex items-center justify-between mb-4">
            <h2 className="text-xl lg:text-2xl font-semibold text-gray-900">
              {availableVouchers.length > 0
                ? resultCountWithVouchers(availableVouchers.length, resultCount, locale, t)
                : `${resultCount} ${offersWord(resultCount, locale, t)}`}
            </h2>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              aria-label={t("listing.sort_label", "Sorteren")}
            >
              <option value="recommended">{t("listing.sort_recommended", "Aanbevolen")}</option>
              <option value="price-asc">{t("listing.sort_price_asc", "Prijs (laag-hoog)")}</option>
              <option value="price-desc">{t("listing.sort_price_desc", "Prijs (hoog-laag)")}</option>
              {discountSortFilterVerified && (
                <option value="discount">{t("listing.sort_discount", "Korting")}</option>
              )}
              <option value="newest">{t("listing.sort_newest", "Nieuwste")}</option>
            </select>
          </div>
          )}

          {/* Active chips */}
          {activeCount > 0 && (
            <ActiveChips
              filters={filters}
              setFilters={setFilters}
              toggleArrayFilter={toggleArrayFilter}
              onClearAll={clearAll}
              t={t}
              currency={currency}
              discountEnabled={discountSortFilterVerified}
            />
          )}

          {/* Mobile result count */}
          {!showRecovery && (
          <div className="lg:hidden mb-3 text-sm text-gray-600">
            {resultCount} {offersWordInline(resultCount, locale, t)}
          </div>
          )}

          {/* Grid */}
          {showRecovery ? (
            <ListingRecovery failed={healFailed} onRetry={healRetry} t={t} />
          ) : resultCount === 0 && honestEmpty && availableDeals.length === 0 && availableVouchers.length > 0 ? (
            // "2 Gutscheine + 0 Angebote" naast "Keine Angebote gefunden" sprak
            // zichzelf tegen: de vouchers zijn het aanbod en staan al in de
            // strook hierboven, de kop noemt de 0 deals eerlijk. Alleen op
            // domeinen met honestEmpty (.de); elders ongewijzigd.
            null
          ) : resultCount === 0 ? (
            <div className="text-center py-16">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                {t("listing.empty_title", "Geen deals gevonden")}
              </h3>
              <p className="text-gray-500 mb-6">
                {t("listing.empty_desc", "Pas je filters aan of bekijk onze andere aanbiedingen.")}
              </p>
              {activeCount > 0 ? (
                <button
                  type="button"
                  onClick={clearAll}
                  className="px-5 py-2.5 text-white text-sm font-medium rounded-lg"
                  style={{ backgroundColor: "#0a8de9" }}
                >
                  {t("listing.clear_filters", "Filters wissen")}
                </button>
              ) : (
                <Link
                  href="/packages/deals"
                  className="px-5 py-2.5 text-white text-sm font-medium rounded-lg inline-block"
                  style={{ backgroundColor: "#0a8de9" }}
                >
                  {t("listing.view_all_deals", "Alle deals bekijken")}
                </Link>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
                {filteredDeals.map((deal, index) => (
                  <DealCard
                    key={deal.deals_id}
                    deal={deal}
                    categoryName={categoryName}
                    filterParams={filterParams}
                    source={source}
                    position={index === 0 ? 0 : undefined}
                    showRecordedBadge={pinnedIdSet.has(String(deal?.deals_id ?? deal?.id ?? ""))}
                  />
                ))}
              </div>
              <p className="text-center text-sm text-gray-500 mt-8">
                {resultCount} {dealsFoundWord(resultCount, locale, t)}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Mobile filter bottom-sheet */}
      {mobileSheetOpen && (
        <MobileFilterSheet
          filters={filters}
          setFilters={setFilters}
          toggleArrayFilter={toggleArrayFilter}
          setSingleFilter={setSingleFilter}
          availableDestinations={availableDestinations}
          availableStars={availableStars}
          activeCount={activeCount}
          resultCount={resultCount}
          onClose={() => setMobileSheetOpen(false)}
          onClearAll={clearAll}
          t={t}
          currency={currency}
          tripDate={tripDate}
          tripNights={tripNights}
          tripAdults={tripAdults}
          tripChildren={tripChildren}
          tripExactDate={tripExactDate}
          updateTripConfig={updateTripConfig}
          discountEnabled={discountSortFilterVerified}
        />
      )}
    </div>
  );
}

/**
 * Herstelstaat voor een lijst die niet opgehaald kon worden. Bewust geen
 * "0 Aanbiedingen" en geen "Geen deals gevonden": de bezoeker weet dat het
 * ophalen loopt en de pagina herstelt zichzelf. Na drie mislukte pogingen een
 * eerlijke foutstaat met een knop.
 */
function ListingRecovery({ failed, onRetry, t }) {
  if (failed) {
    return (
      <div className="text-center py-16" role="alert" data-listing-recovery="failed">
        <h3 className="text-lg font-semibold text-gray-700 mb-2">
          {t("listing.recovery_failed_title", "De aanbiedingen konden niet worden geladen")}
        </h3>
        <p className="text-gray-500 mb-6">
          {t("listing.recovery_failed_desc", "Er ging iets mis bij het ophalen. Probeer het zo nog eens.")}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="px-5 py-2.5 text-white text-sm font-medium rounded-lg"
          style={{ backgroundColor: "#0a8de9" }}
        >
          {t("listing.recovery_retry", "Opnieuw proberen")}
        </button>
      </div>
    );
  }
  return (
    <div data-listing-recovery="loading" role="status" aria-busy="true">
      <p className="text-gray-600 mb-4 text-center">
        {t("listing.recovery_loading", "We halen de aanbiedingen op")}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="rounded-xl border border-gray-200 bg-white overflow-hidden animate-pulse">
            <div className="h-48 bg-gray-200" />
            <div className="p-4 space-y-3">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="h-3 bg-gray-200 rounded w-1/2" />
              <div className="h-5 bg-gray-200 rounded w-1/3" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FilterGroup({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-gray-200 py-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left"
      >
        <h3 className="text-[15px] font-medium text-gray-900">{title}</h3>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

function CheckboxRow({ label, checked, onChange, count }) {
  return (
    <label className="flex items-center justify-between py-1 cursor-pointer">
      <span className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="w-4 h-4 rounded border-gray-300 accent-[#0A8DE9]"
        />
        <span className="text-sm text-gray-700">{label}</span>
      </span>
      {count != null && <span className="text-xs text-gray-400">({count})</span>}
    </label>
  );
}

function PillRow({ label, checked, onChange }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
        checked
          ? "bg-[#2E2F86] border-[#2E2F86] text-white"
          : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
      }`}
    >
      {label}
    </button>
  );
}

function FilterSidebar({
  filters,
  toggleArrayFilter,
  setSingleFilter,
  availableDestinations,
  availableStars,
  activeCount,
  onClearAll,
  t,
  currency,
  discountEnabled,
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-semibold text-gray-900">{t("listing.filters", "Filters")}</h2>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={onClearAll}
            className="text-sm text-red-600 hover:underline"
          >
            {t("listing.clear", "Wissen")}
          </button>
        )}
      </div>

      {availableDestinations.length > 0 && (
        <FilterGroup title={t("listing.filter.destination", "Bestemming")}>
          <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
            {availableDestinations.map((d) => (
              <CheckboxRow
                key={d.label}
                label={d.label}
                count={d.count}
                checked={filters.destinations.includes(d.label)}
                onChange={() => toggleArrayFilter("destinations", d.label)}
              />
            ))}
          </div>
        </FilterGroup>
      )}

      <FilterGroup title={t("listing.filter.price_per_person", "Prijs per persoon")}>
        <div className="flex flex-wrap gap-2">
          {PRICE_BUCKETS.map((b) => (
            <PillRow
              key={b.id}
              label={priceBucketLabel(b, t, currency)}
              checked={filters.priceBuckets.includes(b.id)}
              onChange={() => toggleArrayFilter("priceBuckets", b.id)}
            />
          ))}
        </div>
      </FilterGroup>

      <FilterGroup title={t("listing.filter.included", "Inbegrepen")} defaultOpen={false}>
        <div className="space-y-1">
          {INCLUSION_OPTIONS.map((o) => (
            <CheckboxRow
              key={o.id}
              label={t(o.labelKey, o.label)}
              checked={filters.inclusions.includes(o.id)}
              onChange={() => toggleArrayFilter("inclusions", o.id)}
            />
          ))}
        </div>
      </FilterGroup>

      {availableStars.length > 0 && (
        <FilterGroup title={t("listing.filter.hotel_stars", "Hotel sterren")} defaultOpen={false}>
          <div className="space-y-1">
            {availableStars.map((s) => (
              <CheckboxRow
                key={s}
                label={"★".repeat(s)}
                checked={filters.stars.includes(s)}
                onChange={() => toggleArrayFilter("stars", s)}
              />
            ))}
          </div>
        </FilterGroup>
      )}

      {discountEnabled && (
        <FilterGroup title={t("listing.filter.discount", "Korting")} defaultOpen={false}>
          <div className="flex flex-wrap gap-2">
            {DISCOUNT_BUCKETS.map((b) => (
              <PillRow
                key={b.id}
                label={t(b.labelKey, b.label)}
                checked={filters.discount === b.id}
                onChange={() => setSingleFilter("discount", b.id)}
              />
            ))}
          </div>
        </FilterGroup>
      )}
    </div>
  );
}

function ActiveChips({ filters, toggleArrayFilter, setFilters, onClearAll, t, currency, discountEnabled }) {
  const chips = [];

  filters.destinations.forEach((d) =>
    chips.push({ key: `dest-${d}`, label: d, onRemove: () => toggleArrayFilter("destinations", d) }),
  );
  filters.priceBuckets.forEach((id) => {
    const b = PRICE_BUCKETS.find((x) => x.id === id);
    if (b) chips.push({ key: `price-${id}`, label: priceBucketLabel(b, t, currency), onRemove: () => toggleArrayFilter("priceBuckets", id) });
  });
  filters.inclusions.forEach((id) => {
    const o = INCLUSION_OPTIONS.find((x) => x.id === id);
    if (o) chips.push({ key: `inc-${id}`, label: t(o.labelKey, o.label), onRemove: () => toggleArrayFilter("inclusions", id) });
  });
  filters.stars.forEach((s) =>
    chips.push({ key: `star-${s}`, label: "★".repeat(s), onRemove: () => toggleArrayFilter("stars", s) }),
  );
  if (discountEnabled && filters.discount) {
    const b = DISCOUNT_BUCKETS.find((x) => x.id === filters.discount);
    if (b) chips.push({ key: `disc-${filters.discount}`, label: t(b.labelKey, b.label), onRemove: () => setFilters((p) => ({ ...p, discount: "" })) });
  }

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          onClick={chip.onRemove}
          className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 text-sm text-blue-700 hover:bg-blue-100"
        >
          {chip.label}
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      ))}
      <button
        type="button"
        onClick={onClearAll}
        className="text-sm text-red-600 hover:underline ml-1"
      >
        {t("listing.clear_filters", "Filters wissen")}
      </button>
    </div>
  );
}

function MobileFilterSheet({
  filters,
  toggleArrayFilter,
  setSingleFilter,
  availableDestinations,
  availableStars,
  resultCount,
  onClose,
  onClearAll,
  t,
  currency,
  tripDate,
  tripNights,
  tripAdults,
  tripChildren,
  tripExactDate,
  updateTripConfig,
  discountEnabled,
}) {
  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10">
          <h3 className="font-semibold text-lg text-gray-900">{t("listing.filters", "Filters")}</h3>
          <button onClick={onClose} className="text-gray-500 p-1" aria-label={t("listing.close_sheet", "Sluiten")}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-4 py-4 pb-24">
          <div className="pb-3 mb-3 border-b border-gray-200">
            <TripConfigBlock
              date={tripDate}
              nights={tripNights}
              adults={tripAdults}
              childrenCount={tripChildren}
              exactDate={tripExactDate}
              onChange={updateTripConfig}
            />
          </div>
          <FilterSidebar
            filters={filters}
            toggleArrayFilter={toggleArrayFilter}
            setSingleFilter={setSingleFilter}
            availableDestinations={availableDestinations}
            availableStars={availableStars}
            activeCount={0}
            onClearAll={onClearAll}
            t={t}
            currency={currency}
            discountEnabled={discountEnabled}
          />
        </div>

        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 flex gap-3">
          <button
            onClick={onClearAll}
            className="flex-1 py-3 rounded-lg text-sm font-medium text-gray-700 border border-gray-300"
          >
            {t("listing.clear", "Wissen")}
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-lg text-sm font-semibold bg-[#0A8DE9] text-white"
          >
            {t("listing.show_results", "Toon {count} resultaten", { count: resultCount })}
          </button>
        </div>
      </div>
    </div>
  );
}
